import { useEffect, useMemo, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import auditCompletedIcon from 'assets/auditCompletedIcon.svg';
import auditFilterIcon from 'assets/auditFilterIcon.svg';
import auditProcessingIcon from 'assets/auditProcessingIcon.svg';
import filledFilterIcon from 'assets/filledFilterIcon.svg';

import { getDesignAuditCountsAction } from 'Redux/Actions/designAuditActions';

import { DESIGN_AUDIT_STATUS } from 'utlis/constants';

import styles from './styles.module.scss';

const Overview = ({ onStatusFilter, statusFilter }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const designAuditCounts = useSelector(state => state.designAudit.designAuditCounts);
  const tokenRefreshTimestamp = useSelector(state => state.userStore.tokenRefreshTimestamp);

  useEffect(() => {
    const fetchDesignAuditCounts = async () => {
      setIsLoading(true);

      try {
        await dispatch(getDesignAuditCountsAction());
      }
      finally {
        setIsLoading(false);
      }
    };

    fetchDesignAuditCounts();
  }, [dispatch]);

  const handleFilterClick = val => {
    // Toggle filter: if same status is clicked, remove filter; otherwise set new filter
    onStatusFilter(statusFilter === val ? null : val);
  };

  // Listen for token refresh events and refetch design audit counts
  useEffect(() => {
    if (tokenRefreshTimestamp) {
      const fetchDesignAuditCounts = async () => {
        setIsLoading(true);

        try {
          await dispatch(getDesignAuditCountsAction());
        }
        finally {
          setIsLoading(false);
        }
      };

      fetchDesignAuditCounts();
    }
  }, [tokenRefreshTimestamp, dispatch]);

  const statusCards = useMemo(() => [
    {
      count: designAuditCounts.reviews_this_month,
      label: 'Reviews this month',
      icon: null,
      showFilter: false,
      status: null,
      borderColor: null,
    },
    {
      count: designAuditCounts.reviews_in_progress,
      label: 'Processing',
      icon: auditProcessingIcon,
      showFilter: true,
      status: DESIGN_AUDIT_STATUS.IN_PROGRESS, // Processing status
      borderColor: '#F9C000',
    },
    {
      count: designAuditCounts.reviews_completed,
      label: 'Completed',
      icon: auditCompletedIcon,
      showFilter: true,
      status: DESIGN_AUDIT_STATUS.COMPLETED, // Completed status
      borderColor: '#3EDA6C',
    },
  ], [designAuditCounts]);

  return (
    <div className="mb-[2.5rem]">
      {/* Header with Overview title and New review button */}
      <div className="flex items-center justify-between mb-[1rem]">
        <h2 className="font-inter text-lg font-semibold text-figma-gray-900 leading-6">
          Overview
        </h2>
      </div>

      {isLoading
        ? (
          <div className="flex justify-center items-center h-[80px]">
            <ImpulseSpinner frontColor={'#28BDAE'} />
          </div>
        )
        : (
          <div className="grid grid-cols-3 gap-16">
            {/* Status cards */}
            {statusCards.map((card, index) => (
              <div
                key={index}
                className={`rounded-lg border p-3 relative ${styles['status-card']}`}
                style={{ borderColor: statusFilter === card.status ? card.borderColor : '' }}
              >
                <div className={styles['status-card-top']}>
                  <div className="font-inter text-2xl font-semibold text-figma-gray-900 leading-9">
                    {card.count}
                  </div>
                  {card.showFilter && (
                    <button
                      className="p-1 rounded cursor-pointer"
                      onClick={() => handleFilterClick(card.status)}
                      title={`Filter ${card.label.toLowerCase()} reviews`}
                    >
                      <img
                        src={statusFilter === card.status ? filledFilterIcon : auditFilterIcon}
                        alt="Filter"
                        width={20}
                        height={20}
                      />
                    </button>
                  )}
                </div>
                <div className={styles['status-card-bottom']}>
                  <div className="flex items-center space-x-2">
                    {card.icon && <img src={card.icon} alt={card.label} className="w-4 h-4" /> }
                    <span className="font-inter text-sm font-normal text-figma-gray-900 leading-5">
                      {card.label}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

Overview.propTypes = {
  onStatusFilter: PropTypes.func.isRequired,
  statusFilter: PropTypes.string,
};

export default Overview;
